'use client';

import { useState } from 'react';

import { Collapse } from '@gds/Collapse/Collapse';
import { Close } from '@gds/Icons/Paths/Close';
import { Plus } from '@gds/Icons/Paths/Plus';
import { Typography } from '@gds/Typography/Typography';
import { Scroller } from '@shared-components/Scroller/Scroller';

import type { BrandItemElementProperties, SimplifiedBrands } from '../Brands.entity';
import { BrandItem } from './BrandItem';

import styles from './BrandsExpandable.module.css';

const CORE_COUNT = 6;

const renderItems = (items: BrandItemElementProperties[]) =>
  items.map(brandItem => {
    const label = brandItem.label?.trim();
    if (!label) return null;
    return (
      <BrandItem key={label} label={label} link={brandItem.link} image={brandItem.image?.[0]} />
    );
  });

export const BrandsExpandable = ({ heading, description, brandItems }: SimplifiedBrands) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const items = brandItems ?? [];

  if (!items.length) return null;

  const coreItems = items.slice(0, CORE_COUNT);
  const extraItems = items.slice(CORE_COUNT);

  return (
    <div className={styles.wrapper} data-test-id="brands-expandable-component">
      {heading && (
        <Typography Component="h2" variant="h2" className={styles.heading}>
          {heading}
        </Typography>
      )}
      {description && (
        <Typography variant="body3" className={styles.description}>
          {description}
        </Typography>
      )}

      <div className={styles.mobileScroller}>
        <Scroller scrollItemClass={styles.brandItem}>
          <div className={styles.scrollTrack}>{renderItems(items)}</div>
        </Scroller>
      </div>

      <div className={styles.desktopGrid}>
        <div className={styles.grid}>{renderItems(coreItems)}</div>
        {extraItems.length > 0 && (
          <Collapse open={isExpanded}>
            <div id="brands-extra" className={styles.extraGrid}>
              {renderItems(extraItems)}
            </div>
          </Collapse>
        )}
        {extraItems.length > 0 && (
          <button
            type="button"
            className={styles.toggleButton}
            aria-expanded={isExpanded}
            aria-controls="brands-extra"
            onClick={() => setIsExpanded(prev => !prev)}
          >
            <span className={styles.toggleIcon}>
              {isExpanded ? <Close fontSize={10} /> : <Plus fontSize={10} />}
            </span>
            {isExpanded ? 'Show fewer brands' : 'See more brands'}
          </button>
        )}
      </div>
    </div>
  );
};
